import {
    Avatar,
    Badge,
    Box,
    Divider,
    HStack,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
} from '@chakra-ui/react';
import {
    CheckCircleIcon,
    CheckIcon,
    CloseIcon,
    DeleteIcon,
    DownloadIcon,
    EditIcon,
    InfoIcon,
    SettingsIcon,
    TimeIcon,
} from '@chakra-ui/icons';
import React, { memo } from 'react';

import { ActivityType } from 'types/models';
import ErrorMessage from 'components/ErrorMessage';
import { Link } from 'react-router-dom';
import getActivityCode from 'utils/getActivityCode';
import { stringifySchedule } from 'utils/stringifySchedule';

export interface ActivityTableProps {
    activities: ActivityType[];
    pathname: string;
    showEditAction?: boolean;
    showPresencasAction?: boolean;
    showInscritosAction?: boolean;
    onDeleteClick?: (id: number) => void;
    // Novas ações de download individual
    onDownloadPresencasXls?: (id: number, extension: 'xls' | 'xlsx') => void;
    onDownloadMinistrantesXls?: (id: number, extension: 'xls' | 'xlsx') => void;
    onDownloadListaPresencaPdf?: (id: number) => void;
    onDownloadRelatorioPdf?: (id: number) => void;
    isDownloading?: boolean;
}

const ActivityTable: React.FC<ActivityTableProps> = memo(
    ({
        activities,
        pathname,
        showEditAction,
        showPresencasAction,
        showInscritosAction,
        onDeleteClick,
        onDownloadPresencasXls,
        onDownloadMinistrantesXls,
        onDownloadListaPresencaPdf,
        onDownloadRelatorioPdf,
        isDownloading = false,
    }: ActivityTableProps) => {
        if (activities.length === 0) {
            return <ErrorMessage>Nenhum resultado encontrado.</ErrorMessage>;
        }

        return (
            <TableContainer w="100%" overflowX="auto" border="1px solid" borderColor="gray.200" borderRadius="xl" shadow="md" bg="white" mt={4}>
                <Table variant="simple" size="lg">
                    <Thead bg="gray.50">
                        <Tr>
                            <Th fontSize="xl" py={6} whiteSpace="nowrap">Nome da atividade</Th>
                            {/* <Th fontSize="xl" py={6} whiteSpace="nowrap">Horários</Th> */}
                            <Th fontSize="xl" py={6} whiteSpace="nowrap">Professor responsável</Th>
                            <Th fontSize="xl" py={6} whiteSpace="nowrap">Ministrantes</Th>
                            <Th fontSize="xl" py={6} whiteSpace="nowrap">Categoria</Th>
                            <Th fontSize="xl" py={6} textAlign="center" whiteSpace="nowrap">Presenças emitidas</Th>
                            <Th fontSize="xl" py={6} textAlign="center" whiteSpace="nowrap">Ações</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {activities.map((activity) => (
                            <Tr key={activity.id} _hover={{ bg: 'blue.50' }} transition="all 0.2s" borderBottomWidth="2px" borderColor="gray.200">
                                <Td whiteSpace="nowrap" py={6}>
                                    <Text fontSize="2xl" fontWeight="bold" color="gray.800">{getActivityCode(activity)} - {activity.title}</Text>
                                    {activity.schedules && activity.schedules.length > 0 ? (
                                        <VStack align="start" spacing={1}>
                                            {activity.schedules.map((schedule, i) => (
                                                <HStack key={i} color="gray.700" pt={2}>
                                                    <TimeIcon color="blue.500" boxSize={6} />
                                                    <Text fontSize="2xl" fontWeight="semibold">
                                                        {stringifySchedule(schedule)}
                                                    </Text>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    ) : (
                                        <Text color="gray.400" fontSize="xl">-</Text>
                                    )}
                                </Td>

                                <Td whiteSpace="nowrap" py={6}>
                                    {activity.responsibleUsers && activity.responsibleUsers.length > 0 ? (
                                        <VStack align="start" spacing={2}>
                                            {activity.responsibleUsers.map((r) => (
                                                <HStack key={r.id}>
                                                    <Avatar size="sm" name={r.name} />
                                                    <Text fontSize="xl" fontWeight="medium" color="gray.700">{r.name}</Text>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    ) : (
                                        <Text color="gray.400" fontSize="xl">-</Text>
                                    )}
                                </Td>

                                <Td whiteSpace="nowrap" py={6}>
                                    {activity.teachingUsers && activity.teachingUsers.length > 0 ? (
                                        <VStack align="start" spacing={2}>
                                            {activity.teachingUsers.map((t) => (
                                                <HStack key={t.id}>
                                                    <Avatar size="sm" name={t.name} />
                                                    <Text fontSize="xl" fontWeight="medium" color="gray.700">{t.name}</Text>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    ) : (
                                        <Text color="gray.400" fontSize="xl">Sem ministrantes</Text>
                                    )}
                                </Td>

                                <Td whiteSpace="nowrap" py={6}>
                                    <Badge colorScheme="purple" variant="subtle" fontSize="xl" px={3} py={1} borderRadius="md">
                                        {activity.activityCategory.description}
                                    </Badge>
                                </Td>

                                <Td textAlign="center" whiteSpace="nowrap" py={6}>
                                    {activity.readyForCertificateEmission ? (
                                        <Badge colorScheme="green" px={3} py={2} fontSize="xl" borderRadius="xl" display="inline-flex" alignItems="center" gap={2}>
                                            <CheckIcon boxSize={4} /> Sim
                                        </Badge>
                                    ) : (
                                        <Badge colorScheme="orange" px={3} py={2} fontSize="xl" borderRadius="xl" display="inline-flex" alignItems="center" gap={2}>
                                            <CloseIcon boxSize={4} /> Não
                                        </Badge>
                                    )}
                                </Td>

                                <Td textAlign="center" whiteSpace="nowrap" py={6}>
                                    <Menu placement="bottom-end">
                                        <MenuButton as={IconButton} icon={<SettingsIcon boxSize={6} />} variant="outline" borderWidth="2px" colorScheme="blue" aria-label="Opções" size="lg" _hover={{ bg: 'blue.100' }} />
                                        <MenuList shadow="2xl" zIndex={10} minW="260px" borderRadius="xl" py={2} textAlign="left">

                                            {showEditAction && (
                                                <MenuItem py={4} px={4} fontSize="xl" icon={<EditIcon boxSize={6} mr={2} />} as={Link} to={`${pathname}/${activity.id}/alterar`}>
                                                    Alterar dados
                                                </MenuItem>
                                            )}

                                            {showPresencasAction && (
                                                <MenuItem py={4} px={4} fontSize="xl" icon={<CheckCircleIcon boxSize={6} mr={2} />} as={Link} to={`${pathname}/${activity.id}/presencas`}>
                                                    Presenças
                                                </MenuItem>
                                            )}

                                            {showInscritosAction && (
                                                <MenuItem py={4} px={4} fontSize="xl" icon={<InfoIcon boxSize={6} mr={2} />} as={Link} to={`${pathname}/${activity.id}/inscritos`}>
                                                    Lista de inscritos
                                                </MenuItem>
                                            )}

                                            {/* SEÇÃO DE DOWNLOADS INDIVIDUAIS */}
                                            {(onDownloadPresencasXls || onDownloadMinistrantesXls || onDownloadListaPresencaPdf || onDownloadRelatorioPdf) && (
                                                <Divider my={3} />
                                            )}

                                            {onDownloadPresencasXls && (
                                                <>
                                                    <MenuItem py={4} px={4} fontSize="xl" icon={<DownloadIcon boxSize={6} mr={2} color="green.500" />} isDisabled={isDownloading} onClick={() => onDownloadPresencasXls(activity.id, 'xls')}>
                                                        Presenças (.XLS)
                                                    </MenuItem>
                                                    <MenuItem py={4} px={4} fontSize="xl" icon={<DownloadIcon boxSize={6} mr={2} color="green.500" />} isDisabled={isDownloading} onClick={() => onDownloadPresencasXls(activity.id, 'xlsx')}>
                                                        Presenças (.XLSX)
                                                    </MenuItem>
                                                </>
                                            )}

                                            {onDownloadMinistrantesXls && (
                                                <>
                                                    <MenuItem py={4} px={4} fontSize="xl" icon={<DownloadIcon boxSize={6} mr={2} color="blue.500" />} isDisabled={isDownloading} onClick={() => onDownloadMinistrantesXls(activity.id, 'xls')}>
                                                        Ministrantes (.XLS)
                                                    </MenuItem>
                                                    <MenuItem py={4} px={4} fontSize="xl" icon={<DownloadIcon boxSize={6} mr={2} color="blue.500" />} isDisabled={isDownloading} onClick={() => onDownloadMinistrantesXls(activity.id, 'xlsx')}>
                                                        Ministrantes (.XLSX)
                                                    </MenuItem>
                                                </>
                                            )}

                                            {onDownloadListaPresencaPdf && (
                                                <MenuItem py={4} px={4} fontSize="xl" icon={<DownloadIcon boxSize={6} mr={2} color="red.500" />} isDisabled={isDownloading} onClick={() => onDownloadListaPresencaPdf(activity.id)}>
                                                    Lista de Presenças (.PDF)
                                                </MenuItem>
                                            )}

                                            {onDownloadRelatorioPdf && (
                                                <MenuItem py={4} px={4} fontSize="xl" icon={<DownloadIcon boxSize={6} mr={2} color="red.500" />} isDisabled={isDownloading} onClick={() => onDownloadRelatorioPdf(activity.id)}>
                                                    Relatório de Presenças (.PDF)
                                                </MenuItem>
                                            )}

                                            {/* Excluir */}
                                            {onDeleteClick && (
                                                <>
                                                    <Divider my={3} />
                                                    <Tooltip label={activity.totalRegistry != null && activity.totalRegistry > 0 ? 'Há participantes inscritos nesta atividade.' : ''} hasArrow placement="left" fontSize="lg" p={3} borderRadius="md">
                                                        <Box>
                                                            <MenuItem py={4} px={4} fontSize="xl" icon={<DeleteIcon boxSize={6} mr={2} />} color="red.600" fontWeight="bold" isDisabled={activity.totalRegistry == null || activity.totalRegistry > 0} onClick={() => onDeleteClick(activity.id)}>
                                                                Excluir atividade
                                                            </MenuItem>
                                                        </Box>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </MenuList>
                                    </Menu>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </TableContainer>
        );
    },
);

ActivityTable.displayName = 'ActivityTable';

export default ActivityTable;